import ClosedCaptionIcon from "@mui/icons-material/ClosedCaption";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import GraphicEqIcon from "@mui/icons-material/GraphicEq";
import HeadphonesIcon from "@mui/icons-material/Headphones";
import ShareOutlinedIcon from "@mui/icons-material/ShareOutlined";
import SubscriptionsOutlinedIcon from "@mui/icons-material/SubscriptionsOutlined";
import {
  AccordionDetails,
  AccordionSummary,
  Chip,
  IconButton,
  MenuItem,
  Select,
  SelectChangeEvent,
  Stack,
  styled,
  Tooltip,
  Typography,
} from "@mui/material";
import React, { useMemo, useState } from "react";
import { AclTypes } from "../../../../Core/Api/Types/acl-types";
import {
  TitleContentContent,
  TitleContentContentAudioByEpisode,
  TitleContentContentSubtitleByEpisode,
} from "../../../../Core/Api/Types/db-asset-object-infos";
import { Licences } from "../../../../Core/Api/Types/licences";
import { PermissionType } from "../../../../Core/Api/Types/permission-type";
import { IsObjectShareable, IsPermissionGranted } from "../../../../Core/Utils/asset-object-utils";
import { GetObject } from "../../../../Redux/Reducers/redux-slice-asset";
import {
  GetPlayerInput,
  GetPlayerSetupData,
  PlayPause,
  SetPlayerAutoStart,
  SetPlayerInput,
} from "../../../../Redux/Reducers/redux-slice-react-player";
import { useAppDispatch, useAppSelector } from "../../../../Redux/redux-store";
import { UserHasAcl, UserHasLicence } from "../../../../Utils/helpers";
import SmallAccordion from "../../../Misc/SmallAccordion";
import { ModalContext } from "../../../Modals";
import ModalCreateShare from "../../../Modals/ModalCreateShare/modal-create-share";
import { PlayingStatus } from "../../ReactPlayerWrapper/playing-statuses";
import { useTranslation } from "react-i18next";

export type ConsoleAccordionProps = TitleContentContent & {
  isExpanded: boolean;
  onClickAccordion: () => void;
};

const ConsoleAccordion: React.FC<ConsoleAccordionProps> = (props) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const playerInput = useAppSelector(GetPlayerInput);
  const playerData = useAppSelector(GetPlayerSetupData);
  const obj = useAppSelector(GetObject);
  const [selectedEpisode, setSelectedEpisode] = useState<number>(props.episodeNb?.[0] ?? 0);

  const isplaying = playerData?.parentId === props.id;

  const userCanCreateShare = useMemo(() => {
    if (!obj) return false;
    return (
      IsObjectShareable(obj.childObjects?.find((x) => x.id === props.id)) &&
      UserHasAcl(AclTypes.aclShareCreate) &&
      IsPermissionGranted(obj, PermissionType.Share) &&
      UserHasLicence(Licences.ShareScreening)
    );
  }, [obj]);

  // Modal context
  const modalContext = React.useContext(ModalContext);
  const { openModal } = { ...modalContext };

  const onClickPlay: React.MouseEventHandler<HTMLDivElement> = React.useCallback(
    (e) => {
      //e.stopPropagation();
      if (!isplaying) {
        dispatch(SetPlayerAutoStart(true));
        dispatch(
          SetPlayerInput({
            objectId: props.id,
            episode: selectedEpisode ?? null,
            shareMediaKey: null,
          })
        );
      } else dispatch(PlayPause());

      props.onClickAccordion();
    },
    [props]
  );

  const handleShareContent: React.MouseEventHandler<HTMLButtonElement> = React.useCallback(
    (e) => {
      e.stopPropagation();

      if (!openModal) return;
      openModal(ModalCreateShare, { object: obj, preselectedContentId: props.id });
    },
    [obj]
  );

  const onClickAccordion: React.MouseEventHandler<HTMLButtonElement> = React.useCallback(
    (e) => {
      if (e) {
        e.stopPropagation();
      }
      props.onClickAccordion();
    },
    [props]
  );

  const handleChangeEpisode = (event: SelectChangeEvent<number>) => {
    dispatch(SetPlayerAutoStart(true));
    dispatch(
      SetPlayerInput({
        objectId: props.id,
        episode: event.target.value as number,
        shareMediaKey: null,
      })
    );

    setSelectedEpisode(event.target.value as number);

    console.log("::player episode set = " + event.target.value);
  };

  return (
    <StyledAccordion expanded={props.isExpanded}>
      <StyledAccordionSummary
        expandIcon={
          <IconButton onClick={onClickAccordion}>
            <ExpandMoreRoundedIcon fontSize="small" />
          </IconButton>
        }
        aria-controls="panel1a-content"
        id="panel1a-header"
      >
        <StyledStackTitle
          className="console-accordion-title"
          direction="row"
          alignItems="center"
          onClick={onClickPlay}
          isPlaying={isplaying}
        >
          <PlayingStatus fontSize="small" sx={{ mr: 1.5 }} isplaying={isplaying} />
          <Typography variant="body1" component="h3" fontWeight={500}>
            {props.title}
          </Typography>
        </StyledStackTitle>
        <StyledStackIcons direction="row" alignItems="center" spacing={0}>
          {userCanCreateShare && (
            <IconButton onClick={handleShareContent}>
              <ShareOutlinedIcon fontSize="small" />
            </IconButton>
          )}
        </StyledStackIcons>
      </StyledAccordionSummary>
      <AccordionDetails>
        <ConsoleRow
          title={t("component:consoleRow.title.availableQualities")}
          value={props.qualities}
          type="stringArray"
          episodeNb={selectedEpisode}
        />
        <ConsoleRow
          title={t("component:consoleRow.title.availableAudioMixes")}
          value={props.audio}
          type="audio"
          episodeNb={selectedEpisode}
        />
        <ConsoleRow
          title={t("component:consoleRow.title.availableSubtitles")}
          value={props.subtitles}
          type="subtitle"
          episodeNb={selectedEpisode}
        />

        {props.episodeNb.length > 0 && props.episodeNb[0] !== undefined && (
          <Stack direction="row" sx={{ mb: 1.5 }} alignItems="center">
            <Typography sx={{ flex: 1 }} color="text.tier" variant="body1" component="p">
              {t("common:episode")}
            </Typography>
            <Stack direction="row" sx={{ flex: 1 }} spacing={0.5} justifyContent="flex-end" alignItems="center">
              <Select
                displayEmpty
                IconComponent={ExpandMoreRoundedIcon}
                size="small"
                value={selectedEpisode}
                onChange={(e) => handleChangeEpisode(e)}
                startAdornment={<SubscriptionsOutlinedIcon />}
                MenuProps={{ PaperProps: { sx: { maxHeight: 600 } } }}
              >
                {props.episodeNb.map((index) => (
                  <MenuItem key={index} value={index}>
                    <Typography color="text.tier">{t("menu:item.episodeIndex", { index })}</Typography>
                  </MenuItem>
                ))}
              </Select>
            </Stack>
          </Stack>
        )}
      </AccordionDetails>
    </StyledAccordion>
  );
};

export default ConsoleAccordion;

type ConsoleRowBaseProps = {
  title: string;
  episodeNb: number;
};

type ConsoleRowStringProps = ConsoleRowBaseProps & {
  type: "string";
  value: string;
};

type ConsoleRowStringArrayProps = ConsoleRowBaseProps & {
  type: "stringArray";
  value: string[];
};

type ConsoleRowAudioProps = ConsoleRowBaseProps & {
  type: "audio";
  value: TitleContentContentAudioByEpisode[];
};

type ConsoleRowSubtitleProps = ConsoleRowBaseProps & {
  type: "subtitle";
  value: TitleContentContentSubtitleByEpisode[];
};

type ConsoleRowProps =
  | ConsoleRowStringProps
  | ConsoleRowStringArrayProps
  | ConsoleRowAudioProps
  | ConsoleRowSubtitleProps;

const ConsoleRow: React.FC<ConsoleRowProps> = (props) => {
  const { t } = useTranslation();
  const { title, type, value, episodeNb } = props;

  const display = React.useMemo(() => {
    return (
      <Stack direction="row" sx={{ mb: 1.5 }}>
        <Typography sx={{ flex: 1 }} color="text.tier" variant="body1" component="p">
          {title}
        </Typography>
        <Stack direction="row" spacing={0.5} justifyContent="flex-end" flexWrap="wrap">
          {type === "string" && <Typography color="text.tier">{value}</Typography>}
          {type === "stringArray" && (
            <>
              {value.map((val) => (
                <Chip size="small" label={val} key={val} sx={{ mb: 0.2 }} />
              ))}
            </>
          )}
          {type === "audio" && (
            <>
              {value
                .find((x) => x.episodeNb === episodeNb)
                ?.audios.map((audio, index) => (
                  <Tooltip
                    key={index}
                    arrow
                    title={
                      <Stack p={0.5}>
                        <Typography>
                          <Stack direction="row" spacing={1}>
                            <GraphicEqIcon sx={{ width: 15, height: 15 }} />
                            <Typography>{t("common:mixes")}</Typography>
                          </Stack>
                        </Typography>
                        <Stack direction="row" sx={{ flexWrap: "wrap", gap: 0.5, my: 1 }}>
                          {audio.mixes.map((mix, index) => (
                            <Chip label={mix} key={index} size="small" />
                          ))}
                        </Stack>
                        <Typography mt={2}>
                          <Stack direction="row" spacing={1}>
                            <HeadphonesIcon sx={{ width: 15, height: 15 }} />
                            <Typography>{t("common:accesses")}</Typography>
                          </Stack>
                        </Typography>
                        <Stack direction="row" sx={{ flexWrap: "wrap", gap: 0.5, my: 1 }}>
                          {audio.accesses.map((v, index) => (
                            <Chip label={v} key={index} size="small" />
                          ))}
                          {audio.accesses.length === 0 && (
                            <Typography fontStyle="italic" variant="body2" color="text.tier">
                              {t("common:notAvailable")}
                            </Typography>
                          )}
                        </Stack>
                      </Stack>
                    }
                  >
                    <Chip size="small" label={audio.lang} sx={{ mb: 0.2 }} />
                  </Tooltip>
                ))}
            </>
          )}
          {type === "subtitle" && (
            <>
              {value
                .find((x) => x.episodeNb === episodeNb)
                ?.subtitles.map((subtitle, index) => (
                  <Tooltip
                    key={index}
                    arrow
                    title={
                      <Stack p={0.5}>
                        <Typography>
                          <Stack direction="row" spacing={1}>
                            <ClosedCaptionIcon sx={{ width: 15, height: 15 }} />
                            <Typography>{t("common:types")}</Typography>
                          </Stack>
                        </Typography>
                        <Stack direction="row" sx={{ flexWrap: "wrap", gap: 0.5, my: 1 }}>
                          {subtitle.types.map((v, index) => (
                            <Chip label={v} key={index} size="small" />
                          ))}
                        </Stack>
                      </Stack>
                    }
                  >
                    <Chip size="small" label={subtitle.lang} sx={{ mb: 0.2 }} />
                  </Tooltip>
                ))}
            </>
          )}
        </Stack>
      </Stack>
    );
  }, [title, type, value, episodeNb]);

  return display;
};

const StyledStackIcons = styled(Stack)(({ theme }) => ({
  "&:after": {
    content: '""',
    display: "block",
    width: 1,
    height: 20,
    background: theme.palette.backgroundAlpha.border,
    marginLeft: theme.spacing(1),
    marginRight: theme.spacing(1),
  },
}));

const StyledAccordion = styled(SmallAccordion)(({ theme }) => ({
  border: `1px solid ${theme.palette.backgroundAlpha.border}`,
}));

const StyledAccordionSummary = styled(AccordionSummary)(({ theme }) => ({
  paddingRight: theme.spacing(1),
  paddingLeft: theme.spacing(0),

  ".MuiAccordionSummary-content": {
    margin: 0,
    height: 48,
  },
  "&:hover": {
    background: theme.palette.backgroundAlpha.main,
  },
}));

const StyledStackTitle = styled(Stack, { shouldForwardProp: (prop) => prop !== "isPlaying" })<{
  isPlaying?: boolean;
}>(({ theme, isPlaying }) => ({
  flex: 1,
  height: 48,
  transition: "color .3s",
  cursor: "pointer",
  paddingLeft: theme.spacing(1.5),
  "&:hover": {
    color: theme.palette.primary.text,
  },
  ...(isPlaying && {
    color: theme.palette.primary.main,
    "&:hover": {
      color: theme.palette.primary.main,
    },
  }),
}));
